import { getUseCase } from '@lib/plugin-redux-core';
import { SearchQueryParams } from '@module/form';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import {
  folderNextTokenSelector,
  formNextTokenSelector,
  isLoadingMoreFoldersSelector,
  isLoadingMoreFormsSelector,
  LOAD_MORE_SPACE_OBJECTS_USE_CASE,
} from 'redux/space';
import { DEFAULT_FOLDER_ID, useResolvedFolderId } from './use-resolved-folder-id.hook';

export const useLoadMore = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const resolvedFolderId = useResolvedFolderId();

  const folderNextToken = useSelector(folderNextTokenSelector);
  const formNextToken = useSelector(formNextTokenSelector);
  const isLoadingMoreFolders = useSelector(isLoadingMoreFoldersSelector);
  const isLoadingMoreForms = useSelector(isLoadingMoreFormsSelector);

  const loadMoreUseCase = useMemo(
    () => getUseCase(LOAD_MORE_SPACE_OBJECTS_USE_CASE, dispatch),
    [dispatch],
  );

  const handleLoadMoreFolders = useCallback(() => {
    if (!folderNextToken || isLoadingMoreFolders) return;
    const query: SearchQueryParams = {
      folderId: resolvedFolderId || DEFAULT_FOLDER_ID,
      limit: 20,
      folderNextToken,
      search: router.query.search as string | undefined,
      type: 'folder',
    };
    loadMoreUseCase.execute(query);
  }, [
    folderNextToken,
    isLoadingMoreFolders,
    resolvedFolderId,
    router.query.search,
    loadMoreUseCase,
  ]);

  const handleLoadMoreForms = useCallback(() => {
    if (!formNextToken || isLoadingMoreForms) return;
    const query: SearchQueryParams = {
      folderId: resolvedFolderId || DEFAULT_FOLDER_ID,
      limit: 20,
      formNextToken,
      search: router.query.search as string | undefined,
      type: 'form',
    };
    loadMoreUseCase.execute(query);
  }, [formNextToken, isLoadingMoreForms, resolvedFolderId, router.query.search, loadMoreUseCase]);

  return {
    folderNextToken,
    formNextToken,
    isLoadingMoreFolders,
    isLoadingMoreForms,
    handleLoadMoreFolders,
    handleLoadMoreForms,
  };
};
